import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Download,
  MessageSquare,
  CheckCircle,
  Eye,
  MousePointer,
  XCircle,
  Clock,
  AlertCircle,
  Search,
  ArrowRight,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import ReactECharts from "echarts-for-react";
import useRequest from "../../../components/hook/use-req";
import { Campaign } from "../campaign/types";
import { AnalyticsResponse, DeliveryReport } from "./types";
import { WhatsappMockUp } from "../../../components/marketing/campaign/whatsapp-mock";
import { EmailMockUp } from "../../../components/marketing/campaign/email-mockup";
import { PhoneMockup } from "../../../components/marketing/campaign/phone-mock";
import { FaEnvelope, FaWhatsapp } from "react-icons/fa";
import { RiMessage2Line } from "react-icons/ri";
import { TemplateVariables } from "../create-campaign/types";
import useCrmApi from "../../../components/hook/crm-req";
import { Conversation } from "../../crm-pages/conversation/types";
import { DownloadIcon } from "../../../assets/icon";
import Pagination from "../../../components/pagination";
import { useCreateSegmentFromCampaign } from "../segments/api";

// Per-recipient `button_clicks` comes back as a {button text: count} map —
// rendering it directly crashed React, so the table/CSV show the total.
const totalButtonClicks = (clicks?: Record<string, number> | number | null): number =>
  typeof clicks === "number" ? clicks : clicks ? Object.values(clicks).reduce((sum, n) => sum + (Number(n) || 0), 0) : 0;

// A conversation belongs to RCS when it carries an rcs_account_id or its
// metadata says so — same signal chatarea.tsx uses to detect RCS.
const isRcsConversation = (conv: Conversation): boolean =>
  !!(conv as any).rcs_account_id || (conv as any).conversation_metadata?.channel === "rcs";

// ─── Types ────────────────────────────────────────────────────────────────────
interface SMSCampaignReportProps {
  campaignId: string;
  onClose?: () => void;
  channel?: "sms" | "WHATSAPP" | "email";
  mockupProps?: Record<string, unknown>;
  isExpanded?: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n?: number | null) =>
  n == null || n === undefined ? "N/A" : n.toLocaleString();

const pct = (num?: number | null, den?: number | null) => {
  if (!num || !den || den === 0) return "0.0";
  return ((num / den) * 100).toFixed(1);
};

// Broadcast Stats cards' per-metric download link — disabled (no href, greyed
// out) when that metric's count is 0, since there'd be nothing to download.
const StatDownloadLink: React.FC<{ href?: string; count?: number | null }> = ({ href, count }) =>
  !count ? (
    <span className="text-gray-300 dark:text-gray-600 cursor-not-allowed" title="Nothing to download">
      <DownloadIcon />
    </span>
  ) : (
    <a href={href} className="underline text-blue-600 dark:text-blue-400">
      <DownloadIcon />
    </a>
  );

// Each Broadcast Stats card's "Add to Segment" button — POST
// /segments/campaign/{campaignId} with that card's metric, then hand the
// response's { name, rule_tree } to the segment builder to review/save.
// Disabled when that metric's count is 0 — nothing to build a segment from.
const AddToSegmentButton: React.FC<{ loading: boolean; count?: number | null; onClick: () => void }> = ({ loading, count, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={loading || !count}
    title={!count ? "No contacts for this metric yet" : undefined}
    className="mt-2 w-fit flex items-center justify-center gap-1 text-[11px] font-medium text-[#334155] dark:text-gray-300 border-2 border-[#9BA9B0] dark:border-gray-600 rounded-lg py-1.5 px-3 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
  >
    {loading ? "Adding…" : "Add to Segment +"}
  </button>
);

const formatPhone = (v: string) => {
  if (!v) return "N/A";
  if (v.length <= 12) return v;
  return `${v.slice(0, 6)}...${v.slice(-6)}`;
};

// Only a real delivery time counts — a failed DLR (or one with no
// delivered_at yet) shows a dash rather than borrowing created_at.
const isFailedDlr = (r: { status?: string }) => ["FAILED", "UNDELIVERED", "REJECTED", "BOUNCED"].includes(String(r.status || "").toUpperCase());
const deliveredAt = (r: { status?: string; delivered_at?: string | null }) => (isFailedDlr(r) || !r.delivered_at ? "—" : fmtDate(r.delivered_at));

const fmtDate = (v?: string | null) => {
  if (!v) return "N/A";
  try {
    return new Date(v).toLocaleString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return v;
  }
};

// ─── Status badge ─────────────────────────────────────────────────────────────
const STATUS_CONFIG: Record<
  string,
  { bg: string; text: string; dot: string; Icon: React.ElementType }
> = {
  DELIVERED: { bg: "bg-emerald-50 dark:bg-emerald-500/15", text: "text-emerald-700 dark:text-emerald-400", dot: "bg-emerald-500", Icon: CheckCircle },
  OPENED: { bg: "bg-violet-50 dark:bg-violet-500/15", text: "text-violet-700 dark:text-violet-400", dot: "bg-violet-500", Icon: Eye },
  CLICKED: { bg: "bg-blue-50 dark:bg-blue-500/15", text: "text-blue-700 dark:text-blue-400", dot: "bg-blue-500", Icon: MousePointer },
  REPLIED: { bg: "bg-purple-50 dark:bg-purple-500/15", text: "text-purple-700 dark:text-purple-400", dot: "bg-purple-500", Icon: MessageSquare },
  FAILED: { bg: "bg-red-50 dark:bg-red-500/15", text: "text-red-700 dark:text-red-400", dot: "bg-red-500", Icon: XCircle },
  SENT: { bg: "bg-amber-50 dark:bg-amber-500/15", text: "text-amber-700 dark:text-amber-400", dot: "bg-amber-400", Icon: Clock },
  PENDING: { bg: "bg-yellow-50 dark:bg-yellow-500/15", text: "text-yellow-700 dark:text-yellow-400", dot: "bg-yellow-400", Icon: Clock },
  UNDELIVERED: { bg: "bg-gray-50 dark:bg-gray-800", text: "text-gray-600 dark:text-gray-300", dot: "bg-gray-400", Icon: AlertCircle },
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cfg = STATUS_CONFIG[status] ?? STATUS_CONFIG["SENT"];
  const { Icon } = cfg;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
      <Icon size={11} />
      {status.charAt(0) + status.slice(1).toLowerCase()}
    </span>
  );
};

// ─── Donut chart card ─────────────────────────────────────────────────────────
interface DonutCardProps {
  title: string;
  total: number;
  totalLabel: string;
  series: { name: string; value: number; color: string }[];
  // value null = not reported by the backend; shown as "—" with no percentage
  legend: { name: string; value: number | null; pct: string; color: string }[];
  compact?: boolean;
}
const DonutCard: React.FC<DonutCardProps> = ({ title, total, totalLabel, series, legend, compact }) => {
  const option = {
    tooltip: { trigger: "item", formatter: "{b}: {c} ({d}%)" },
    legend: { show: false },
    series: [
      {
        type: "pie",
        radius: ["58%", "82%"],
        avoidLabelOverlap: false,
        label: { show: false },
        emphasis: { label: { show: false } },
        data: series.map((s) => ({ value: s.value, name: s.name, itemStyle: { color: s.color } })),
      },
    ],
  };

  const chartSize = compact ? "80px" : "112px";

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-4 flex flex-col gap-3">
      <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{title}</span>
      {!compact && (
        <div className="text-center">
          <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">{fmt(total)}</span>
          <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500">{totalLabel}</p>
        </div>
      )}
      <div className={`flex ${compact ? "flex-col items-center" : "items-center"} gap-4`}>
        <div className="relative shrink-0" style={{ width: chartSize, height: chartSize }}>
          <ReactECharts option={option} style={{ width: chartSize, height: chartSize }} />
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-base font-bold text-gray-900 dark:text-gray-100">{fmt(total)}</span>
            <span className="text-[9px] text-[#7B7B7B] font-semibold dark:text-gray-500 text-center leading-tight">{totalLabel}</span>
          </div>
        </div>
        <div className="flex flex-col gap-1.5 flex-1 w-full">
          {legend.map((l) => (
            <div key={l.name} className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: l.color }} />
                <span className="text-[10px] text-gray-600 dark:text-gray-300">{l.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-semibold text-gray-800 dark:text-gray-200">{l.value == null ? "—" : fmt(l.value)}</span>
                {l.value != null && <span className="text-[9px] text-[#7B7B7B] font-semibold dark:text-gray-500">{l.pct}%</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── Funnel card ──────────────────────────────────────────────────────────────
interface FunnelCardProps {
  title: string;
  total?: number;
  steps: { label: string; value: number; color: string }[];
}
const FunnelCard: React.FC<FunnelCardProps> = ({ title, total, steps }) => {
  const option = {
    tooltip: { trigger: "item", formatter: "{b}: {c}" },
    series: [
      {
        type: "funnel",
        left: "10%",
        width: "80%",
        minSize: "20%",
        maxSize: "100%",
        sort: "none",
        gap: 2,
        label: { show: false },
        labelLine: { show: false },
        itemStyle: { borderWidth: 0 },
        data: steps.map((s) => ({ value: s.value, name: s.label, itemStyle: { color: s.color } })),
      },
    ],
  };
  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-4 flex flex-col gap-2">
      <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{title}</span>
      {total != null && (
        <div>
          <span className="text-2xl font-bold text-gray-900 dark:text-gray-100">{fmt(total)}</span>
          <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500">Total Contacts</p>
        </div>
      )}
      <ReactECharts option={option} style={{ height: "150px" }} />
      <div className="flex flex-col gap-1">
        {steps.map((s) => (
          <div key={s.label} className="flex items-center justify-between">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />
              <span className="text-[10px] text-gray-500 dark:text-gray-400">{s.label}</span>
            </div>
            <span className="text-[10px] font-semibold text-gray-700 dark:text-gray-300">{fmt(s.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────────
const CampaignReport: React.FC<SMSCampaignReportProps> = ({
  campaignId,
  onClose,
  channel = "sms",
  isExpanded = false,
}) => {
  const id = campaignId;
  const token = localStorage.getItem("authToken");
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { makeRequest: createSegmentFromCampaign } = useCreateSegmentFromCampaign();
  const [addingSegmentMetric, setAddingSegmentMetric] = useState<string | null>(null);

  // FastAPI's 422 `detail` is a string for most errors but an array of
  // {type, loc, msg, ctx} objects for validation errors (like the metric
  // enum here) — passing that array straight into toast.error() crashed the
  // whole screen ("Objects are not valid as a React child"), same class of
  // bug fixed elsewhere this session.
  const extractErrorMessage = (payload: any, fallback: string): string => {
    const detail = payload?.detail ?? payload?.message;
    if (!detail) return fallback;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((d) => d?.msg || JSON.stringify(d)).join(", ") || fallback;
    if (typeof detail === "object") return detail.msg || fallback;
    return fallback;
  };

  const handleAddToSegment = async (metric: string) => {
    if (!id) return;
    setAddingSegmentMetric(metric);
    try {
      // source_scope/negate/occurred_in_days are all part of the request body
      // now (Segments API, 2026-08-05) — "all" + not-negated + "ever" is the
      // sensible default for a plain "Add to Segment" click from the report.
      // `channel` narrows the returned rule_tree's Broadcast Delivered/Clicked
      // condition to this specific broadcast's channel (see segment-builder.tsx's
      // "Channel" narrowing UI) — this report screen already knows it
      // (campaign_channel beats the generic `channel` prop when both exist).
      const [response, status] = await createSegmentFromCampaign(
        {
          source_scope: "all",
          metric,
          negate: false,
          occurred_in_days: null,
          // `campaign` state is never actually populated (setCampaign is
          // dead code in this file), so it was silently falling through to
          // the `channel` prop's "sms" default every time. `a?.channel` —
          // the real fetched analytics response — is what every other
          // channel check in this file already trusts (see Broadcast
          // Details' Channel field, the mockup's channel switch, etc).
          channel: (a?.channel || channel || "").toLowerCase() || undefined,
        },
        undefined,
        id,
      );
      if (status < 200 || status >= 300) {
        toast.error(extractErrorMessage(response, "Failed to create segment"));
        return;
      }
      const draft = response?.data || response;
      sessionStorage.setItem("pendingSegmentDraft", JSON.stringify(draft));
      toast.success(response?.message || "Segment created successfully");
      navigate("/segments");
    } finally {
      setAddingSegmentMetric(null);
    }
  };

  const [DLReport, setDLReport] = useState<DeliveryReport[]>([]);
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"contacts" | "conversations">("contacts");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [analytics, setAnalytics] = useState<AnalyticsResponse | null>(null);
  const [reachExpanded, setReachExpanded] = useState(true);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const params = new URLSearchParams(new URL(window.location.href).search);
  const [currentPage, setCurrentPage] = useState(Number(params.get("page") || 1));
  const [pageSize, setPageSize] = useState(10);

  // /campaign/dlrlog is paginated server-side (page/limit in, `pagination`
  // out — it defaults to 10 rows), so each page is its own request. Status is
  // sent too, and also re-applied to the returned page in case it's ignored.
  const [dlrTotalPages, setDlrTotalPages] = useState(1);
  const filteredDLR = useMemo(
    // A replied row may still carry its delivery status (DELIVERED, OPENED…),
    // so REPLIED also matches on replied_at / reply_text.
    () =>
      DLReport.filter(
        (r) =>
          statusFilter === "all" ||
          r.status === statusFilter ||
          (statusFilter === "REPLIED" && (!!r.replied_at || !!r.reply_text))
      ),
    [DLReport, statusFilter]
  );
  const totalPages = Math.max(1, dlrTotalPages);
  const pagedDLR = filteredDLR;

  const { makeRequest: getReport } = useRequest(`/campaign/dlrlog`, "GET", { Authorization: `Bearer ${token}` });
  const { makeRequest: getSummary } = useRequest(`/campaign/analytics`, "GET", { Authorization: `Bearer ${token}` });
  const { makeRequest: getInConversation } = useCrmApi(`/conversations`, "GET", { Authorization: `Bearer ${token}` });


  // Debounce the shared search box before it hits the conversations API —
  // same 400ms pattern used elsewhere in the app, so switching contacts <->
  // conversations tabs or the DLR table's own client-side filter (below)
  // aren't affected, only this network call.
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchTerm(searchTerm), 400);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // GET /conversations returns a flat list of the `limit` most recent, with
  // no cursor — so "Load more" re-requests with a bigger limit (same as the
  // main Conversations page), and a short page means there's nothing left.
  // The backend rejects limit > 200, so loading stops there.
  const CONVERSATIONS_PAGE = 50;
  const CONVERSATIONS_MAX = 200;
  const [conversationsLimit, setConversationsLimit] = useState(CONVERSATIONS_PAGE);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [hasMoreConversations, setHasMoreConversations] = useState(false);

  useEffect(() => {
    setConversationsLimit(CONVERSATIONS_PAGE);
  }, [campaignId, debouncedSearchTerm]);

  useEffect(() => {
    let cancelled = false;
    const fetchConversations = async () => {
      setConversationsLoading(true);
      try {
        const [response] = await getInConversation(undefined, {
          campaign_id: campaignId,
          campaign_replied: true,
          limit: conversationsLimit,
          ...(debouncedSearchTerm.trim() ? { search: debouncedSearchTerm.trim() } : {}),
        });
        if (cancelled) return;
        // An error body (e.g. a validation `detail`) keeps what's already shown.
        if (!Array.isArray(response) && !Array.isArray(response?.results)) {
          setHasMoreConversations(false);
          return;
        }
        const list: Conversation[] = Array.isArray(response) ? response : response.results;
        setConversations(list);
        setHasMoreConversations(list.length >= conversationsLimit && conversationsLimit < CONVERSATIONS_MAX);
      } catch (error) {
        if (!cancelled) setHasMoreConversations(false);
      } finally {
        if (!cancelled) setConversationsLoading(false);
      }
    };

    if (campaignId) fetchConversations();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId, debouncedSearchTerm, conversationsLimit]);

  useEffect(() => {
    getSummary(undefined, { campaign_id: id }).then(([res]) => setAnalytics(res));
  }, []);

  const fetchDLR = async (showPaginationLoader = false, page = currentPage, limit = pageSize) => {
    try {
      if (showPaginationLoader) {
        setIsPaginationLoading(true);
      } else {
        setInitialLoading(true);
      }

      const p: Record<string, unknown> = {
        campaign_id: id,
        page,
        limit,
        ...(statusFilter !== "all" ? { status: statusFilter } : {}),
        ...(debouncedSearchTerm.trim() ? { search: debouncedSearchTerm.trim() } : {}),
      };
      const [response] = await getReport(undefined, p);
      if (response?.dlrs && Array.isArray(response.dlrs)) {
        setDLReport(response.dlrs);
        const pg = response.pagination;
        setDlrTotalPages(Number(pg?.pages) || Math.max(1, Math.ceil((Number(pg?.total) || response.dlrs.length) / limit)));
      }
    } catch {
      // Handle error
    } finally {
      setInitialLoading(false);
      setIsPaginationLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    if (id) {
      fetchDLR(false);
    }
  }, [id]);

  // A new search or status filter goes back to page 1 and refetches.
  useEffect(() => {
    if (!id || initialLoading) return;
    setCurrentPage(1);
    fetchDLR(true, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, statusFilter]);

  async function handlePageChange(page: number) {
    // Store the current scroll position
    const scrollContainer = document.querySelector('.overflow-y-auto');
    const scrollPosition = scrollContainer?.scrollTop || 0;

    setCurrentPage(page);
    fetchDLR(true, page);

    // Restore scroll position after the next render
    requestAnimationFrame(() => {
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollPosition;
      }
    });
  }

  const a = analytics?.response;

  // Extract data from analytics response
  const total = a?.total ?? 0;
  const delivered = a?.delivered ?? 0;
  const undelivered = a?.undelivered ?? 0;
  const totalClicks = a?.total_clicks ?? 0;
  const subscribedContacts = a?.subscribed_contacts;
  const unsubscribedContacts = a?.unsubscribed_contacts;
 
  // Use performance object for percentages if available, otherwise calculate
  const perf = a?.performance;
  const opened = perf?.opened?.count ?? a?.opened ?? 0;
  const replied = perf?.replied?.count ?? a?.replied ?? 0;
  // Distinct contacts who clicked, not total click events (a contact can
  // click more than once) — matches what "Add to Segment" actually builds
  // a segment out of. Confirmed real fields (2026-08-18).
  const urlClicks = a?.unique_url_clickers ?? perf?.url_clicks?.unique ?? perf?.url_clicks?.count ?? a?.url_clicks ?? 0;
  const btnClicks = a?.unique_button_clickers ?? perf?.button_clicks?.unique ?? perf?.button_clicks?.count ?? a?.button_clicks ?? 0;
  const bounced = perf?.bounced?.count ?? a?.bounced ?? 0;
  const failed = perf?.failed?.count ?? a?.failed ?? 0;
  // Derived rather than trusted from the API's own `pending` field, which
  // doesn't reliably reconcile against total/delivered/failed/undelivered —
  // "Pending" is whatever's left of the audience that hasn't landed in one
  // of the other three buckets yet.
  const pending = Math.max(total - (delivered + failed + undelivered), 0);


  // ─── Extract content for message preview ──────────────────────────────────
  const content = a?.content;
  const messageText = content?.message ?? "";
  const buttons = content?.buttons ?? [];
  // The actual sender identity for the preview header — never the campaign's
  // own name (that's what the campaign is called internally, not who the
  // recipient sees as the sender), matching every other preview surface
  // (Messaging step, Preview & Launch) which all key off sender_name.
  const senderDisplayName = a?.sender_name || undefined;

  // Email-specific fields
  const subject = content?.subject ?? "";
  const previewText = content?.preview_text ?? "";
  const htmlText = content?.html_text ?? "";
  const plainText = content?.plain_text ?? "";


  // For WhatsAppMockUp - single CTA object (not array)
  const whatsappCta = buttons && buttons.length > 0 ? {
    label: buttons[0].text || "Learn More",
    url: buttons[0].url || "#"
  } : undefined;



  // For SMS/Phone, use the first button or a default
  const phoneCta = buttons && buttons.length > 0 ? {
    label: buttons[0].text || "Learn More",
    url: buttons[0].url || "#"
  } : undefined;

  const exportCSV = () => {
    const rows = [
      ["Phone", "Status", "Operator", "Clicks", "URL Clicks", "Error Code", "Error Message", "Sent At", "Delivered At", "Read At", "Clicked At"],
      ...filteredDLR.map((r) => [
        r.phone_number,
        r.status,
        r.operator,
        totalButtonClicks(r.button_clicks),
        r.url_clicks ?? 0,
        r.err_code ?? "",
        r.err_message ?? "",
        r.sent_at ?? r.created_at ?? "",
        isFailedDlr(r) ? "" : r.delivered_at ?? "",
        r.read_at ?? "",
        r.clicked_at ?? r.first_click_at ?? "",
      ]),
    ];
    const blob = new Blob([rows.map((r) => r.join(",")).join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `campaign_${id}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  // ── Shared chart data ──────────────────────────────────────────────────────
  const reachedSeries = [
    { name: "Delivered", value: delivered, color: "#10b981" },
    { name: "Pending", value: pending, color: "#f59e0b" },
    { name: "Undelivered", value: undelivered, color: "#6b7280" },
  ];
  const reachedLegend = reachedSeries.map((s) => ({ ...s, pct: pct(s.value, total) }));

  const engagedSeries = [
    { name: "Opened", value: opened, color: "#10b981" },
    { name: "Replied", value: replied, color: "#3b82f6" },
    { name: "URL Clicks", value: urlClicks, color: "#8b5cf6" },
    { name: "Btn Clicks", value: btnClicks, color: "#f59e0b" },
  ];
  // Engagement (opened/replied/clicked) only ever happens among delivered
  // messages, so that's the denominator — not totalClicks, which is a much
  // smaller number that made Opened alone come out over 100%.
  const engagedLegend = engagedSeries.map((s) => ({ ...s, pct: pct(s.value, delivered || total) }));

  // One row per button on the sent template (content.buttons). Analytics only
  // returns the combined button_clicks total, so per-button counts are read
  // from a breakdown keyed by button text if the backend sends one; until
  // then each button shows "—" and the total sits on its own row.
  const BUTTON_COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#06b6d4", "#ef4444", "#84cc16"];
  const templateButtons = (a?.content?.buttons || []).map((b) => b?.text).filter(Boolean) as string[];
  const buttonBreakdown: Record<string, number> | undefined =
    (perf?.button_clicks as any)?.by_button ?? (perf?.button_clicks as any)?.breakdown ?? (a as any)?.button_clicks_breakdown;
  const buttonItems: { label: string; value: number | null; color: string }[] = templateButtons.map((text, i) => ({
    label: `Button: ${text}`,
    value: buttonBreakdown ? buttonBreakdown[text] ?? 0 : null,
    color: BUTTON_COLORS[i % BUTTON_COLORS.length],
  }));
  if (!buttonBreakdown || !templateButtons.length) {
    buttonItems.push({ label: templateButtons.length ? "All button clicks" : "Button clicks", value: btnClicks, color: "#64748b" });
  }

  const interactionItems = [
    ...buttonItems,
    { label: "Click through", value: urlClicks, color: "#8b5cf6" },
    { label: "Potential Leads", value: a?.potential_leads ?? 0, color: "#ec4899" },
  ];

  const funnelSteps = [
    { label: "Delivered", value: delivered, color: "#10b981" },
    { label: "Read", value: opened, color: "#3b82f6" },
    { label: "Click throughs", value: urlClicks, color: "#8b5cf6" },
    { label: "Potential Leads", value: a?.potential_leads ?? 0, color: "#f59e0b" },
  ];

  // Already only replied conversations — the backend filters with
  // campaign_replied=true.
  const filteredConversations = useMemo(
    () =>
      [...conversations].sort(
        (a, b) => new Date(b.last_message_at || b.created_at).getTime() - new Date(a.last_message_at || a.created_at).getTime()
      ),
    [conversations],
  );

  // Show initial loader only
  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="p-4 sm:p-6">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-h-[92vh] overflow-y-auto flex flex-col">

        {/* ── Modal Header ────────────────────────────────────────────────── */}
        <div className="sticky top-0 z-10 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 px-4 sm:px-6 py-4 flex items-center justify-between gap-3 rounded-t-2xl">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors"
              style={{
                backgroundColor:
                  (a?.channel || channel || "").toUpperCase() === "WHATSAPP" ? "#25D366" :
                    (a?.channel || channel || "").toUpperCase() === "EMAIL" ? "#4285F4" :
                      "#10B981"
              }}
            >
              {(() => {
                const ch = (a?.channel || channel || "").toUpperCase();

                if (ch === "WHATSAPP") {
                  return <FaWhatsapp className="w-5 h-5 text-white" />;
                }
                if (ch === "EMAIL") {
                  return <FaEnvelope className="w-5 h-5 text-white" />;
                }
                // Default: SMS
                return <MessageSquare className="w-4 h-4 text-white" />;
              })()}
            </div>

            <div className="min-w-0">
              <h2 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-gray-100 truncate">
                {a?.campaign_name ?? campaign?.campaign_name ?? "Broadcast Report"}
              </h2>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                <span className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 truncate">
                  Sent on {fmtDate(a?.created_at ?? campaign?.scheduled_start)}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {isExpanded && (
              <button
                onClick={exportCSV}
                className="hidden sm:flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors"
              >
                <Download className="w-3.5 h-3.5" />
                Download
              </button>
            )}
          </div>
        </div>
        {/* ── Body ────────────────────────────────────────────────────────── */}
        <div className="flex flex-1 overflow-hidden">

          {/* ── Main column ────────────────────────────────────────────────── */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-5 min-w-0">

            {/* Broadcast Details */}
            <section>
              <h3 className="text-xs font-semibold text-[#7B7B7B]  dark:text-gray-500 uppercase tracking-wider mb-3">Broadcast Details</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-4">
                <div>
                  <p className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500">Channel</p>
                  <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 capitalize">{a?.channel?.toLowerCase() ?? channel}</p>
                </div>
                <div>
                  <p className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500">Target Audience</p>
                  <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">{a?.campaign_name ?? "N/A"}</p>
                </div>
                <div>
                  <p className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500">Total Contacts</p>
                  <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">{fmt(total)}</p>
                </div>
                <div>
                  <p className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500">Total Cost</p>
                  <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">{a?.currency_symbol} {a?.total_cost ?? "N/A"}</p>
                </div>
                <div>
                  <p className="text-[11px] text-[#7B7B7B] font-semibold dark:text-gray-500">Performance</p>
                  <div>
                    <p className="text-sm font-bold text-gray-800 dark:text-gray-200">{pct(delivered, total)}%</p>
                    <div className="w-12 h-0.5 bg-green-500 rounded my-1" />
                    <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500">High Performing ↑</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Broadcast Stats */}
            <section>
              <h3 className="text-xs font-semibold text-[#7B7B7B] dark:text-gray-500 uppercase tracking-wider mb-3">Broadcast Stats</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                  <p className="text-[12px] flex justify-between font-medium text-[#3A3428] dark:text-gray-300 mb-1">Total Contacts</p>
                  <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{fmt(total)}</p>
                  <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500">Targeted Contacts</p>
                </div>


                {unsubscribedContacts != null && (
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                    <p className="text-[12px] flex justify-between font-medium text-[#3A3428] dark:text-gray-300 mb-1">Unsubscribed</p>
                    <div className="flex items-end gap-1 flex-wrap">
                      <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{fmt(unsubscribedContacts)}</p>
                      <p className="text-sm font-semibold text-red-500 dark:text-red-400 mb-0.5">{pct(unsubscribedContacts, total)}%</p>
                    </div>
                    <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500">Of Total Contacts</p>
                  </div>
                )}

                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                  <p className="text-[12px] flex justify-between font-medium text-[#3A3428] dark:text-gray-300 mb-1">Delivered
                    <StatDownloadLink href={a?.performance?.delivered?.download_url} count={delivered} />
                  </p>
                  <div className="flex items-end gap-1 flex-wrap">
                    <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{fmt(delivered)}</p>
                    <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400 mb-0.5">{pct(delivered, total)}%</p>
                  </div>
                  <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500">Of Total Contacts</p>
                  <AddToSegmentButton loading={addingSegmentMetric === "delivered"} count={delivered} onClick={() => handleAddToSegment("delivered")} />
                </div>

                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                  <p className="text-[12px] flex justify-between font-medium text-[#3A3428] dark:text-gray-300 mb-1">Pending
                    <StatDownloadLink href={a?.performance?.pending?.download_url} count={pending} />
                  </p>
                  <div className="flex items-end gap-1 flex-wrap">
                    <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{fmt(pending)}</p>
                    <p className="text-sm font-semibold text-amber-500 dark:text-amber-400 mb-0.5">{pct(pending, total)}%</p>
                  </div>
                  <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500">Of Total Contacts</p>
                  {/* No "pending" (or equivalent) in the Segments API's
                      accepted metric enum ('sent'|'delivered'|'read'|
                      'undelivered'|'failed'|'dnd'|'bounced'|'complained'|
                      'clicked'|'replied') — a button here would always 422. */}
                </div>

                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                  <p className="text-[12px] flex justify-between font-medium text-[#3A3428] dark:text-gray-300 mb-1">Undelivered
                    <StatDownloadLink href={a?.performance?.undelivered?.download_url} count={undelivered} />
                  </p>
                  <div className="flex items-end gap-1 flex-wrap">
                    <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{fmt(undelivered)}</p>
                    <p className="text-sm font-semibold text-red-500 dark:text-red-400 mb-0.5">{pct(undelivered, total)}% </p>
                  </div>
                  <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500">Of Total Contacts</p>
                  <AddToSegmentButton loading={addingSegmentMetric === "undelivered"} count={undelivered} onClick={() => handleAddToSegment("undelivered")} />
                </div>

                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                  <p className="text-[12px] flex justify-between font-medium text-[#3A3428] dark:text-gray-300 mb-1">Opened
                    <StatDownloadLink href={a?.performance?.opened?.download_url} count={opened} />
                  </p>
                  <div className="flex items-end gap-1 flex-wrap">
                    <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{fmt(opened)}</p>
                    <p className="text-sm font-semibold text-blue-600 dark:text-blue-400 mb-0.5">{pct(opened, delivered)}%</p>
                  </div>
                  <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500">Of Delivered Audience</p>
                  <AddToSegmentButton loading={addingSegmentMetric === "opened"} count={opened} onClick={() => handleAddToSegment("read")} />
                </div>

                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                  <p className="text-[12px] flex justify-between font-medium text-[#3A3428] dark:text-gray-300 mb-1">Replied
                    <StatDownloadLink href={a?.performance?.replied?.download_url} count={replied} />
                  </p>
                  <div className="flex items-end gap-1 flex-wrap">
                    <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{fmt(replied)}</p>
                    <p className="text-sm font-semibold text-purple-600 dark:text-purple-400 mb-0.5">{pct(replied, delivered)}%</p>
                  </div>
                  <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500">Of Delivered Audience</p>
                  <AddToSegmentButton loading={addingSegmentMetric === "replied"} count={replied} onClick={() => handleAddToSegment("replied")} />
                </div>

                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                  <p className="text-[12px] flex justify-between font-medium text-[#3A3428] dark:text-gray-300 mb-1">URL Clicks
                    <StatDownloadLink href={a?.performance?.url_clicks?.download_url} count={urlClicks} />
                  </p>
                  <div className="flex items-end gap-1 flex-wrap">
                    <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{fmt(urlClicks)}</p>
                    <p className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 mb-0.5">{pct(urlClicks, delivered)}%</p>
                  </div>
                  <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500">Of Delivered Audience</p>
                  {/* The Segments API's metric enum doesn't distinguish URL
                      vs button clicks — both map to the single "clicked"
                      value, same as the Button Clicks card below. */}
                  <AddToSegmentButton loading={addingSegmentMetric === "url_clicks"} count={urlClicks} onClick={() => handleAddToSegment("unique_url_clickers")} />
                </div>

                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                  <p className="text-[12px] flex justify-between font-medium text-[#3A3428] dark:text-gray-300 mb-1">Button Clicks
                    <StatDownloadLink href={a?.performance?.button_clicks?.download_url} count={btnClicks} />
                  </p>
                  <div className="flex items-end gap-1 flex-wrap">
                    <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{fmt(btnClicks)}</p>
                    <p className="text-sm font-semibold text-teal-600 dark:text-teal-400 mb-0.5">{pct(btnClicks, delivered)}%</p>
                  </div>
                  <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500">Of Delivered Audience</p>
                  <AddToSegmentButton loading={addingSegmentMetric === "button_clicks"} count={btnClicks} onClick={() => handleAddToSegment("unique_button_clickers")} />
                </div>

                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3"> 
                  <p className="text-[12px] flex justify-between font-medium text-[#3A3428] dark:text-gray-300 mb-1">Bounced
                    <StatDownloadLink href={a?.performance?.bounced?.download_url} count={bounced} />
                  </p>
                  <div className="flex items-end gap-1 flex-wrap">
                    <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{fmt(bounced)}</p>
                    <p className="text-sm font-semibold text-teal-600 dark:text-teal-400 mb-0.5">{pct(bounced, total)}%</p>
                  </div>
                  <AddToSegmentButton loading={addingSegmentMetric === "bounced"} count={bounced} onClick={() => handleAddToSegment("bounced")} />
                  <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500">Of Bounced Audience</p>
                </div>

                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3">
                  <p className="text-[12px] flex justify-between font-medium text-[#3A3428] dark:text-gray-300 mb-1">Rejected
                    <StatDownloadLink href={a?.performance?.failed?.download_url} count={failed} />
                  </p>
                  <div className="flex items-end gap-1 flex-wrap">
                    <p className="text-xl font-bold text-gray-900 dark:text-gray-100">{fmt(failed)}</p>
                    <p className="text-sm font-semibold text-red-600 dark:text-red-400 mb-0.5">{pct(failed, total)}%</p>
                  </div>
                  <p className="text-[10px] text-[#7B7B7B] font-semibold dark:text-gray-500">Of Rejected Audience</p>
                </div>

              </div>
            </section>

            {/* ── Contacts Log / Conversations (ONLY when expanded) ────── */}
            {isExpanded && (
              <section>
                <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 dark:border-gray-700 mb-4">
                  {(["contacts", "conversations"] as const).map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${activeTab === tab
                        ? "border-blue-600 text-blue-600 dark:text-blue-400"
                        : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                        }`}
                    >
                      {tab === "contacts" ? "Contacts Log" : "Incoming Conversations"}
                    </button>
                  ))}
                  <div className="ml-auto relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7B7B7B] font-semibold dark:text-gray-500 w-3.5 h-3.5" />
                    <input
                      type="text"
                      placeholder={activeTab === "contacts" ? "Search Contacts" : "Search Conversations"}
                      className="pl-9 pr-3 py-2 text-sm border border-gray-200 dark:border-gray-700 bg-white dark:bg-transparent text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-44 sm:w-56"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>

                {activeTab === "contacts" ? (
                  <>
                    <div className="mb-3">
                      <select
                        className="px-3 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-900"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                      >
                        <option value="all">All Status</option>
                        <option value="DELIVERED">Delivered</option>
                        <option value="OPENED">Opened</option>
                        <option value="CLICKED">Clicked</option>
                        <option value="REPLIED">Replied</option>
                        <option value="FAILED">Failed</option>
                        <option value="SENT">Sent</option>
                        <option value="PENDING">Pending</option>
                      </select>
                    </div>

                    <div className="overflow-x-auto rounded-xl border border-gray-100 dark:border-gray-800">
                      <table className="w-full text-sm min-w-[980px]">
                        <thead className="bg-gray-50 dark:bg-gray-800">
                          <tr>
                            {["Contact", "Status", "Sent At", "Delivered At", "Read At", "Clicked At", "Button Clicks", "Url Clicks"].map((h) => (
                              <th key={h} className="px-4 py-3 text-left text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide whitespace-nowrap">
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                          {isPaginationLoading ? (
                            <tr>
                              <td colSpan={8} className="px-4 py-8 text-center">
                                <div className="flex items-center justify-center gap-2">
                                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600" />
                                  <span className="text-sm text-[#7B7B7B] font-semibold dark:text-gray-500">Loading...</span>
                                </div>
                              </td>
                            </tr>
                          ) : filteredDLR.length === 0 ? (
                            <tr>
                              <td colSpan={8} className="px-4 py-8 text-center text-[#7B7B7B] font-semibold dark:text-gray-500 text-sm">
                                {DLReport.length === 0 ? "No DLR data available" : "No records match your search"}
                              </td>
                            </tr>
                          ) : (
                            pagedDLR.map((r, i) => (
                              <tr key={`${r.message_id}-${i}`} className="hover:bg-gray-50/80 dark:hover:bg-gray-800/50 transition-colors">
                                <td className="px-4 py-3">
                                  <span className="text-blue-600 dark:text-blue-400 text-xs font-mono hover:underline cursor-pointer">
                                    +{formatPhone(r.phone_number)}
                                  </span>
                                </td>
                                <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                                <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                  {fmtDate(r.sent_at || r.created_at)}
                                </td>
                                <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                  {deliveredAt(r)}
                                </td>
                                <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                  {fmtDate(r.read_at)}
                                </td>
                                <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                                  {fmtDate(r.clicked_at || r.first_click_at)}
                                </td>
                                <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-300">
                                  {totalButtonClicks(r?.button_clicks)}
                                </td>
                                <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-300">
                                  {r?.url_clicks ?? 0}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>

                    <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800">
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        pageSize={pageSize}
                        isLoading={isPaginationLoading}
                        onPageSizeChange={(size) => {
                          setPageSize(size);
                          setCurrentPage(1);
                          fetchDLR(true, 1, size);
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div className="space-y-3">
                    {filteredConversations.length > 0 && (
                      <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                        {filteredConversations.length.toLocaleString()} replied
                      </p>
                    )}
                    {filteredConversations.length === 0 ? (
                      (conversationsLoading || !hasMoreConversations) && (
                        <div className="text-center py-12 text-[#7B7B7B] font-semibold dark:text-gray-500 text-sm border border-dashed border-gray-200 dark:border-gray-700 rounded-2xl">
                          {conversationsLoading ? "Loading conversations…" : "No replies for this campaign"}
                        </div>
                      )
                    ) : (
                      filteredConversations
                        .map((conv) => {
                          const isRcs = isRcsConversation(conv);
                          return (
                          <div
                            key={conv.id}
                            className="group flex items-start gap-4 p-4 bg-white dark:bg-gray-900 border border-gray-100 dark:border-gray-800 hover:border-gray-200 dark:hover:border-gray-700 rounded-2xl hover:shadow-sm cursor-pointer transition-all"

                          >
                            {isRcs ? (
                              <RiMessage2Line className="w-6 h-6 text-[#5E35B1] mt-0.5" />
                            ) : (
                              <FaWhatsapp className="w-6 h-6 text-[#25D366] mt-0.5" />
                            )}

                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <p className="min-w-0 truncate font-medium text-gray-900 dark:text-gray-100">{conv.title}</p>
                                <span className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 whitespace-nowrap">
                                  {fmtDate(conv.last_message_at || conv.created_at)}
                                </span>
                              </div>

                              <div className="mt-1.5">
                                <p className="text-base text-[#89857E] dark:text-gray-400 line-clamp-2">
                                  <span>{conv.last_message?.direction === "outbound" ? "Last message:" : "Customer replied:"}</span>{" "}
                                  {conv.last_message?.body || "No message content"}
                                </p>
                              </div>
                              <button
                                className="text-blue-900 dark:text-blue-400 font-medium underline"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  window.dispatchEvent(
                                    new CustomEvent("navigate-to-conversation", {
                                      detail: {
                                        conversationId: conv.id,
                                        phoneNumber: conv.external_thread_id,
                                        title: conv.title,
                                        campaignId,
                                        channel: isRcs ? "rcs" : "whatsapp",
                                      },
                                    }),
                                  );
                                }}
                              >
                                View Chat
                              </button>
                            </div>

                            <div className="opacity-0 group-hover:opacity-100 transition-opacity self-center">
                              <ArrowRight className="w-5 h-5 text-[#7B7B7B] font-semibold dark:text-gray-500" />
                            </div>
                          </div>
                          );
                        })
                    )}
                    {!hasMoreConversations && !conversationsLoading && filteredConversations.length > 0 && (
                      <p className="text-center text-xs text-gray-500 dark:text-gray-400 pt-1">
                        {conversations.length >= CONVERSATIONS_MAX
                          ? `Showing replies from the latest ${CONVERSATIONS_MAX} conversations`
                          : "No more replies"}
                      </p>
                    )}
                    {hasMoreConversations && (
                      <div className="flex justify-center pt-1">
                        <button
                          type="button"
                          onClick={() => setConversationsLimit((n) => Math.min(n + CONVERSATIONS_PAGE, CONVERSATIONS_MAX))}
                          disabled={conversationsLoading}
                          className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-60"
                        >
                          {conversationsLoading ? "Loading…" : "Load more"}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </section>
            )}


            {/* ── View Detailed Report Button ────────────────────────────── */}
            {!isExpanded && (
              <button
                onClick={() => {
                  if (onClose) {
                    const drawer = document.querySelector('.drawer-fullscreen-toggle');
                    if (drawer) {
                      (drawer as HTMLElement).click();
                    }
                  }
                }}
                className="w-full flex items-center justify-center gap-2 py-3 border border-gray-200 dark:border-gray-700 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                View Detailed Report <ArrowRight className="w-4 h-4" />
              </button>
            )}

            {/* ── Audience Summary ───────────────────────────────────────── */}
            <section>
              <h3 className="text-xs font-semibold text-[#7B7B7B] dark:text-gray-500 uppercase tracking-wider mb-3">Audience Summary</h3>

              {/* ALWAYS show the first 2 donut charts */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <DonutCard
                  title="Contacts Reached"
                  total={total}
                  totalLabel="Total contacts"
                  series={reachedSeries}
                  legend={reachedLegend}
                />
                <DonutCard
                  title="Contacts Engaged"
                  total={delivered || total}
                  totalLabel="Total contacts"
                  series={engagedSeries}
                  legend={engagedLegend}
                />
              </div>

              {/* ── Hidden content: only shown when expanded ── */}
              {isExpanded && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <DonutCard
                    title="Interaction Summary"
                    // URL clicks + button clicks — was reusing totalClicks
                    // (a small, mostly-unrelated number) and showing the
                    // exact same total as the Contacts Engaged card above.
                    total={urlClicks + btnClicks}
                    totalLabel="Total Clicks"
                    series={interactionItems.map((item) => ({
                      name: item.label,
                      value: item.value ?? 0,
                      color: item.color,
                    }))}
                    legend={interactionItems.map((item) => {
                      const totalValue = urlClicks + btnClicks;
                      return {
                        name: item.label,
                        value: item.value,
                        pct: totalValue > 0 && item.value != null ? ((item.value / totalValue) * 100).toFixed(1) : "0.0",
                        color: item.color,
                      };
                    })}
                  />
                  <FunnelCard
                    title="Potential Leads"
                    total={a?.potential_leads ?? 0}
                    steps={funnelSteps}
                  />
                </div>
              )}
            </section>

            {/* ── Performance Overview ──────────────────────────────────── */}
            <section>
              <h3 className="text-xs font-semibold text-[#7B7B7B]  dark:text-gray-500 uppercase tracking-wider mb-3">Performance Overview</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Audience reach overview</h4>
                  <ul className="space-y-1 text-xs text-gray-600 dark:text-gray-300">
                    <li>• A total of {fmt(total)} contacts were targeted across multiple broadcasts</li>
                    <li>• {fmt(delivered)} messages were successfully delivered</li>
                    <li>• Delivery rate achieved: {pct(delivered, total)}%</li>
                  </ul>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Audience engagement overview</h4>
                  <ul className="space-y-1 text-xs text-gray-600 dark:text-gray-300">
                    <li>• The broadcast generated direct contact engagement through CTA buttons</li>
                    <li>• {fmt(totalClicks)} contacts expressed interest in our offers and services</li>
                    <li>• Read rate is {pct(opened, delivered)}% and CTA click rate is {pct(totalClicks, delivered)}%</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* ── Next Best Actions ────────────────────────────────────── */}
            <section>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Immediate Follow-ups</h4>
                  <ul className="space-y-1 text-xs text-gray-600 dark:text-gray-300 mb-3">
                    <li>• Contact the interested contacts within the 24–48 hours window</li>
                    <li>• 1st reason for contacting based on business</li>
                    <li>• 2nd reason for contacting based on business</li>
                  </ul>
                  <button className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-xs font-medium text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
                    Save as New Segment
                  </button>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Broadcast Improvements</h4>
                  <ul className="space-y-1 text-xs text-gray-600 dark:text-gray-300 mb-3">
                    <li>• Schedule broadcasts between 6PM–8PM for better engagement</li>
                    <li>• Improve CTA copies for better engagement, e.g. "Call us", "Tap to share interest"</li>
                    <li>• Use marketing images for better engagements</li>
                  </ul>
                  <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors">
                    Re-engage Contacts
                  </button>
                </div>
              </div>
            </section>
          </div>



          {/* ── Right column: Message Preview — ONLY in expanded mode ────── */}
          {isExpanded && (
            <div className="hidden lg:flex w-72 shrink-0 border-l border-gray-100 dark:border-gray-800 p-5 flex-col gap-4 overflow-y-auto">
              <h3 className="text-xs font-semibold text-[#7B7B7B] dark:text-gray-500 uppercase tracking-wider">Message Preview</h3>

              <div className="p-4 border-gray-100 dark:border-gray-800">
                <button
                  onClick={() => setReachExpanded(!reachExpanded)}
                  className="w-full flex items-center justify-between"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900 dark:text-gray-100 text-sm">Your Estimated Reach</span>
                    <span className="w-3 h-3 bg-green-500 rounded-full block" />
                  </div>
                  {reachExpanded ? (
                    <ChevronUp className="w-4 h-4 text-[#7B7B7B] font-semibold dark:text-gray-500" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-[#7B7B7B] font-semibold dark:text-gray-500" />
                  )}
                </button>

                {reachExpanded && (
                  <div className="mt-3 space-y-3">


                    <div className="grid grid-cols-2 gap-3">
                      {/* {channel === 'sms' && (
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">SMS Parts</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{smsSegments}</p>
                </div>
              )} */}

                      <div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Campaign size</p>
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {/* {a?.currency_symbol} {(a?.total_cost ?? 0).toFixed(2)} */}
                          {a?.total_contacts}
                        </p>
                      </div>
                    </div>

                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                      <p className="text-xs text-gray-500 dark:text-gray-400">Total cost</p>
                      <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                        {a?.currency_symbol}
                        {a?.total_cost != null
                          ? a.total_cost.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })
                          : "0.00"}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex-1 flex items-start justify-center">

                {(() => {
                  const currentChannel = (a?.channel || channel || "").toUpperCase();

                  if (currentChannel === "WHATSAPP") {
                    return (
                      <WhatsappMockUp
                        message={messageText}
                        vars={a?.content?.variables ?
                          Object.entries(a.content.variables).reduce((acc, [key, value]) => {
                            acc[`variable_${key}` as keyof TemplateVariables] = value as string;
                            return acc;
                          }, {} as TemplateVariables)
                          : undefined
                        }
                        cta={whatsappCta}
                        senderName={senderDisplayName || "Business"}
                        template={{
                          buttons: content?.buttons,
                          header_type: content?.header_type,
                          header_content: content?.header_value,
                          footer: content?.footer,
                        }}
                      />
                    );
                  }

                  if (currentChannel === "EMAIL") {
                    return (
                      <EmailMockUp
                        subject={subject || "No Subject"}
                        previewText={previewText || ""}
                        senderName={senderDisplayName || "DALM"}
                        htmlContent={htmlText || messageText}
                        plainText={plainText || messageText}
                      />
                    );
                  }

                  // Default: SMS / Phone
                  return (
                    <PhoneMockup
                      message={messageText}
                      senderName={senderDisplayName || "DALM"}
                      cta={phoneCta}
                    />
                  );
                })()}

              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignReport;